/**
 * BridgeLostBanner - says out loud why every control just went dead.
 *
 * THE INCIDENT THIS EXISTS FOR. On 2026-09-10, two hours into a five hour
 * set, the rig stopped responding. Every control was unpressable, the BPM was
 * stuck, the logo dot kept pulsing. The operator read that as "the app froze"
 * and force-quit it.
 *
 * That is EXACTLY what a completely healthy app looks like when it loses its
 * bridge, because `FireButton` is `disabled={!live()}` and `live()` requires a
 * connected bridge (DEC-124 Clause A). Reproduced deliberately in
 * `_probe_bridge_loss_symptom.ts`: cut the link and every control greys, the
 * BPM stops, the dot keeps pulsing, and the app is fine the whole time.
 *
 * So the app already KNEW. It just never said so anywhere the operator was
 * looking. The header pill does change to "offline" / "0/2 up", but it is a
 * small chip in the chrome bar while the thing commanding attention is a
 * screenful of dead buttons with no stated reason. Under stage light, mid
 * set, that reads as a crash.
 *
 * WHAT THIS FIXES, and what it deliberately does not. It does not repair the
 * connection - the reconnect machinery was measured healing a full 142 second
 * outage in ~18 seconds and is deliberately untouched. It removes the
 * AMBIGUITY: a dead-looking rig now states whether it is disconnected and
 * trying, which is the difference between "wait a moment" and "force-quit
 * mid-song".
 *
 * The elapsed counter is load-bearing, not decoration. A number that visibly
 * advances is proof the JavaScript is running, which is the very question
 * three sessions failed to answer from outside the device. If this banner is
 * on screen and its timer is climbing, the app is alive and the network is
 * the problem. If it is on screen and FROZEN, the page itself has stopped -
 * and that is the first time that distinction has been visible from the front
 * of the room.
 *
 * Renders only while disconnected with at least one bridge configured, so it
 * adds nothing to the normal path. A never-paired rig is a different state
 * with its own existing empty-state copy, and is not hijacked here.
 */

import { Show, createSignal, onCleanup } from 'solid-js';
import { bridges, bridgeConnectedReactive, getConnection, connectionsVersion } from '../opbridge/store.ts';

export function BridgeLostBanner() {
  // Ticks once a second purely so `elapsed` re-renders. This is the liveness
  // proof: if the number stops moving, the page is dead rather than offline.
  const [now, setNow] = createSignal(Date.now());
  const t = setInterval(() => setNow(Date.now()), 1000);
  onCleanup(() => clearInterval(t));

  let lostAt: number | null = null;

  // PLAIN FUNCTIONS, not createMemo, and that is load-bearing. The memo
  // version of this read `true` forever: measured in one page at one instant,
  // the canvas Button was `disabled=true` while this still said connected, so
  // the banner stayed hidden on a rig whose every control was dead. A memo
  // caches and keeps its subscription to whatever it read first, and the
  // connection object is REPLACED in `connections` on a disconnect - so the
  // memo sat subscribed to a dead object's signal that would never change
  // again. FireButton has always used plain arrows (`const live = () => ...`)
  // for exactly this path; matching that idiom is what makes the banner and
  // the controls agree. See the project's "Solid: Map read tracks nothing"
  // note - a Map lookup is not a tracked dependency.
  const configured = () => bridges().length;

  // REUSE the predicate items use, never a reimplementation of it. The first
  // version of this read `getConnection(id).statusSignal()()` by hand and
  // disagreed with the controls in exactly the state that matters: measured
  // 27s into a full outage, every control was disabled while this still said
  // connected, so the banner stayed hidden on a dead rig. `store.ts` says of
  // `bridgeConnectedReactive` that an item's "am I live" indicator and its
  // actual send target "can never disagree" - that guarantee only holds for
  // callers who go through it, which is now what this does.
  //
  // Also deliberately NOT `anyConnectedReactive()`: despite the name it checks
  // only the PRIMARY, so on a mesh it would cry "bridge lost" while a peer was
  // still connected and firing. A banner that lies about a working rig is
  // worse than no banner.
  // `connectionsVersion()` is read FIRST, and that is the whole trick. It is
  // the store's own documented remedy: "callers that need to react to a swap
  // should read this signal before calling getConnection(), purely to register
  // the dependency". Without it this read sticks forever, because
  // `bridgeConnectedReactive` ends in `connections.get(id)` - a Map lookup,
  // which Solid cannot track - and the connection OBJECT is REPLACED on a
  // reconnect, leaving any subscription pointed at a dead object's signal.
  //
  // Measured, not theorised: before this line the banner read `connected` for
  // 147 seconds of total network loss while the console showed
  // `[opbridge] connected -> unreachable` and every control was disabled.
  const connected = () => {
    connectionsVersion();
    return bridges().some((b) => bridgeConnectedReactive(b.id));
  };

  const show = () => configured() > 0 && !connected();

  const elapsed = () => {
    now(); // track the tick
    if (!show()) { lostAt = null; return ''; }
    if (lostAt === null) lostAt = Date.now();
    const secs = Math.max(0, Math.round((Date.now() - lostAt) / 1000));
    if (secs < 60) return `${secs}s`;
    const m = Math.floor(secs / 60);
    return `${m}m ${String(secs % 60).padStart(2, '0')}s`;
  };

  // The host always renders (`display: contents`, so it costs no layout) and
  // publishes the two inputs to its own decision. Without this, "the banner
  // did not appear" is indistinguishable from "the component never mounted",
  // and a probe has to guess which - which is exactly what happened the first
  // time this was tested. Now the decision is readable from the DOM.
  return (
    <div
      class="bridge-lost-host"
      data-configured={configured()}
      data-connected={connected() ? 'true' : 'false'}
      data-show={show() ? 'true' : 'false'}
      data-default-live={String(bridgeConnectedReactive(undefined))}
      data-raw-status={bridges().map((b) => getConnection(b.id)?.statusSignal()() ?? 'no-conn').join('|')}
    >
    <Show when={show()}>
      <div class="bridge-lost" role="status" aria-live="polite">
        <span class="bridge-lost-title">BRIDGE LOST</span>
        <span class="bridge-lost-detail">
          Controls are disabled because nothing is connected. Reconnecting.
        </span>
        {/* Monospaced + tabular so the digits do not jitter, which would make
            a running clock look like a stuttering one. */}
        <span class="bridge-lost-elapsed">{elapsed()}</span>
      </div>
    </Show>
    </div>
  );
}
